import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb } from "react-bootstrap";
import { Tooltip } from "react-tooltip";
import { ActionHandlerFunction } from "@preveil-api";
import {
  Account, DefaultRoutes, PathInfo, PathList, GenericEntryEntity, DriveUIActionTypes, DriveEntryType, buildNavigationLink, DriveConstantsType, useGetPathList,
  PrivateRoutes, WatchedTabs, FullNodePath, DriveConstants
} from "src/common";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  entry: GenericEntryEntity;
  full_path_list?: FullNodePath;
  watched_node_ids?: string[];
  path_info?: PathInfo[];
  root_item?: string;
  root_url?: string;
  root_tooltip?: string;
  tooltip_id?: string;
  drive_module?: DriveConstantsType; // NOTE: where should the link in breadcrumbs point to (Drive vs Watched)
  handleAction: ActionHandlerFunction;
}

function BreadcrumbsComponent(props: AllProps) {
  const { current_account, entry, full_path_list, path_info, watched_node_ids, drive_module, root_item, tooltip_id, root_url, root_tooltip, handleAction } = props; // 
  const [_entry, setEntry] = useState<GenericEntryEntity>(entry); // NOTE: needs local state to prevent entry obj override
  const [_path_info, setPathInfo] = useState<PathInfo[] | undefined>(path_info);
  const { buildBreadcrumbs, breadcrumbs_paths } = useGetPathList(current_account);

  useEffect(() => {
    if (!!full_path_list && !path_info) {
      setEntry(entry);
      // NOTE: Decrypt the Pathnodes from the call
      buildBreadcrumbs(full_path_list, drive_module);
    }
  }, [entry]);

  useEffect(() => {
    setPathInfo(path_info);
  }, [path_info]);

  // Description: Upon fully decrypted, update the main Entry in ListView
  useEffect(() => {
    !!breadcrumbs_paths && handleSuccessBreadcrumbs(breadcrumbs_paths.slice());
  }, [breadcrumbs_paths]);

  // Description: Handle the successful return from getPathInfo and filter out when watched_node_ids is not undefined
  function handleSuccessBreadcrumbs(_breadcrumbs_paths: PathInfo[]) {
    // NOTE: Keep only the part returned in the GET_WATCHED_NODE_PATH
    const path_info_filtered = !!watched_node_ids ? PathList.filterNodes(_breadcrumbs_paths, watched_node_ids) : _breadcrumbs_paths;
    const new_entry = { ..._entry, ...{ path_info: path_info_filtered, full_path_list: undefined } };
    setPathInfo(new_entry.path_info);
    handleAction({ actionType: DriveUIActionTypes.SetPathInfo, params: { id: new_entry.id, path_info: new_entry.path_info } });
  }

  // Description: Get the full path list for tooltip
  function getTooltip(): string {
    const _tooltip_string = (!!_path_info && _path_info?.length) ?
      _.map(_path_info, ((node, i) => (`${node.path} ${i < Number(_path_info.length - 1) ? ">" : ""}`))).join(" ") : _entry.name;
    const _root_tooltip = !!root_tooltip ? `${root_tooltip} > ` : "";
    return `${_root_tooltip}${_tooltip_string}`;
  }

  // Description: Build File type last node url to MyPreVeil link
  function getEntityURLbyType(node: PathInfo) {
    return (_entry.type === DriveEntryType.FILE && _entry.id === node.id && !!_entry.parent_id) ?
      buildNavigationLink(_entry.collection_id, _entry.parent_id, _entry.type, _entry.id) : (node.url || DefaultRoutes.drive_root);
  }

  return <>
    <Breadcrumb className="drive-breadcrumbs"
      data-tooltip-id={!!tooltip_id ? tooltip_id : "bc-tooltip"}
      data-tooltip-content={getTooltip()}
      data-tooltip-place="top">
      {
        !!root_item &&
        <>
          <Breadcrumb.Item
            className="bc-title"
            onClick={(e) => { e.stopPropagation(); }}
            linkAs={Link}
            linkProps={{
              to: !!root_url ? root_url : `/drive/${PrivateRoutes.watched_route}/${WatchedTabs.root}`
            }}>{root_item}</Breadcrumb.Item>
        </>
      }
      {
        !!_path_info &&
        _.map(_path_info, (node: PathInfo, index: number) => {
          const is_last = Number(_path_info.length - 1) === index;
          const is_root = index === 0 && !root_item;
          // NOTE: Render the node if the drive_module is watched since it will return only DIR types or if its for My PreVeil then only return the links
          const render_node = drive_module === DriveConstants.WATCHED || (!drive_module && !node.is_true_root);
          return render_node ?
            <Breadcrumb.Item key={`path_${index}`}
              onClick={(e) => { e.stopPropagation(); }}
              className={is_last ? "last" : is_root ? "bc-title" : ""}
              linkAs={Link}
              linkProps={{ to: (!!entry.is_deleted && is_last ? "" : getEntityURLbyType(node)) }}>
              {node.path}</Breadcrumb.Item> : null;
        })
      }

    </Breadcrumb>
    <Tooltip
      id={!!tooltip_id ? tooltip_id : "bc-tooltip"}
      disableStyleInjection="core"
      closeEvents={{
        mouseleave: true,
        blur: true,
        click: true,
        dblclick: true,
        mouseup: true,
      }} />
  </>;
}

export default React.memo(BreadcrumbsComponent);
